(ns app.front.view.reset-password.reset
  (:require [app.front.state-machine :as sm]
            [app.front.ui.password :as ui-password]
            [app.front.validate :as validate]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]))

(def model {:user/password   [[validate/NonBlankString :validate/blank-password]]
            ::password-score [[validate/PasswordScore :validate/password-score]
                              [validate/PasswordBad :validate/password-bad]]})


(def initial-form-data {:user/password  ""
                        ::state         nil
                        ::show-errors?  false
                        ::errors        {}
                        ::server-errors nil})

(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::set-form-data ::client-validation
                        ::try-submit    ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-failure ::server-errors
                        ::submit-success ::done}
   ::server-errors     {::set-form-data ::client-validation}})


;; -- Subscriptions -----------------------------------------------------------


(rf/reg-sub ::form-data
  (fn [db [_ path]]
    (let [form-data (::form-data db)]
      (if path (get-in form-data path) form-data))))


(rf/reg-sub ::state
  :<- [::form-data]
  (fn [form-data _] (get form-data ::state)))


(rf/reg-sub ::show-errors?
  :<- [::form-data]
  (fn [form-data _] (get form-data ::show-errors?)))


(rf/reg-sub ::server-errors
  :<- [::form-data]
  (fn [form-data _] (get form-data ::server-errors)))


(rf/reg-sub ::submit-disabled?
  :<- [::state]
  (fn [state _] (= state ::submitting)))


;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine
                                                  [::form-data ::state]))

(def debug
  (rf/after
    (fn [db event]
      (js/console.debug "=======")
      (js/console.debug "state: " (str (-> db ::form-data ::state)))
      (js/console.debug "event: " (str event)))))


(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])


(rf/reg-event-db
  ::init
  interceptors
  (fn [db [event user-data]]
    (-> db
        (update ::form-data #(merge % user-data initial-form-data))
        (update-next-state event))))


(rf/reg-event-fx
  ::set-form-data
  interceptors
  (fn [{:keys [db]} [event path value]]
    {:db       (-> db
                   (assoc-in (into [::form-data] path) value)
                   (update-next-state event))
     :dispatch [::validate-fields]}))


(rf/reg-event-fx
  ::submit-clicked
  interceptors
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [::form-data ::show-errors?] true)
     :dispatch (if-not (empty? (validate/validate-form (::form-data db) model))
                 [::validate-fields]
                 [::try-submit])}))


(rf/reg-event-fx
  ::validate-fields
  interceptors
  (fn [{:keys [db]} _]
    {:dispatch (if (or (false? (get-in db [::form-data ::show-errors?]))
                       (empty? (validate/validate-form (::form-data db) model)))
                 [::no-errors]
                 [::still-errors])}))


(rf/reg-event-fx ::try-submit
  (fn [{:keys [db]} [event _]]
    {:db           (update-next-state db event)
     :comm/command {:command        :user/reset-password
                    :data           (-> db
                                        ::form-data
                                        (select-keys [:user/token :user/password])
                                        validate/strip-nil-values)
                    :on-success     [::submit-success]
                    :on-bad-request [::submit-failure.bad-request]
                    :on-not-found   [::submit-failure.not-found]}}))


(rf/reg-event-fx ::submit-success
  (fn [{:keys [db]} [_ _ {:keys [mfa] :as user}]]
    (if-let [mfa-url (:redirect-url mfa)]
      {:navigate-to mfa-url}
      {:db       (assoc db :session/user user)
       :dispatch [::add-notification-go-to-dashboard]})))


(rf/reg-event-fx ::add-notification-go-to-dashboard
  (fn [_ _]
    {:dispatch   [:notifications/add
                  :inline
                  :success
                  :profile/updated
                  :cloudpermit/start]
     :route/push :dashboard}))


(rf/reg-event-fx
  ::submit-failure.bad-request
  interceptors
  (fn [{:keys [db]} [event status body]]
    (js/console.error "::submit-failure" status body)
    {:db (update-next-state db event)}))


(rf/reg-event-fx
  ::submit-failure.not-found
  interceptors
  (fn [_ _]
    {:dispatch   [:notifications/add
                  :inline
                  :warning
                  :warning/warning
                  :error/not-found]
     :route/push :login}))


(rf/reg-event-db ::no-errors interceptors update-next-state)
(rf/reg-event-db ::still-errors interceptors update-next-state)


(defn view [user-data]
  (rf/dispatch-sync [::init user-data])
  (let [tr (<sub [:tr])]
    (fn []
      [:div.cloudpermit-page
       [:div.page-header
        [:h1.my-8 (tr [:forgot-password/heading])]]
       [:form
        {:on-submit   (fn [e] (.preventDefault e) (>evt [::submit-clicked]))
         :no-validate true}
        [:section.flex.flex-col.w-full
         [:fieldset
          [:legend.mb-6.bx--type-heading-03.pl-2 (tr [:forgot-password/select-new])]
          [:div.flex.flex-wrap.bx--col-xs-12.bx--col-sm-6.bx--col-md-5.bx--col-lg-4.bx--col-xxl-3.mb-6
           [ui-password/password-input
            {:tr              tr
             :id              "input-password"
             :label           (tr [:user/password])
             :auto-complete   "new-password"
             :handle-change   (fn [password]
                                (>evt [::set-form-data [:user/password] password]))
             :on-score-change #(>evt [::set-form-data [::password-score] %])
             :use-meter?      true}]]]
         [:div.flex.flex-wrap.bx--col-xs-12.bx--col-sm-6.bx--col-md-5.bx--col-lg-4.bx--col-xxl-3.mb-6
          (cc/button
            {:type             :submit
             :disabled         (<sub [::submit-disabled?])
             :icon             i/check
             :icon-description (tr [:forgot-password/login])}
            (tr [:forgot-password/login]))]
         [:div.bx--col-xs-12.bx--col-sm-6.bx--col-md-5.bx--col-lg-4.bx--col-xxl-3
          (when (and (<sub [::show-errors?])
                     (seq (validate/validate-form (<sub [::form-data]) model tr)))
            [:ul.mb-6
             (for [error (validate/validate-form (<sub [::form-data]) model tr)]
               [:li.mb-2.text-red
                {:key (str error)}
                [cc/icon {:icon        i/remove
                          :description (tr [:forgot-password/login])
                          :fill        "currentcolor"
                          :class-name  "mr-2"
                          :height      "14"
                          :width       "14"}]
                (str error)])])]]]])))
